import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, isPresenter, isVerifiedSchoolStaff } from '../contexts/AuthContext';
import ShadedSelect from '../components/ui/ShadedSelect';
import { useLinkCopiedToast } from '../hooks/useLinkCopiedToast';

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

function roleLabel(role, t) {
  if (role === 'Tea') return t('report.teacher');
  if (role === 'Sta') return t('report.staff');
  return t('report.student');
}

function SchoolDashboard() {
  const { t } = useTranslation();
  const { copyInviteLink, linkCopiedToast } = useLinkCopiedToast();
  const { currentUser, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [inviteRole, setInviteRole] = useState('Stu');
  const [inviteLabel, setInviteLabel] = useState('');
  const [creatingInvite, setCreatingInvite] = useState(false);

  const isUserPresenter = isPresenter(currentUser);
  const isUserSchoolStaff = isVerifiedSchoolStaff(currentUser);
  const acceptCode = searchParams.get('accept');

  const selectedSchool = useMemo(
    () => schools.find((s) => s.school_id === selectedSchoolId) || null,
    [schools, selectedSchoolId]
  );

  const loadSchools = useCallback(async () => {
    const res = await fetch('/api/schools', { headers: authHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to load schools');
    setSchools(data.schools || []);
    if (data.schools?.length === 1) {
      setSelectedSchoolId(data.schools[0].school_id);
    } else if (currentUser?.school_id) {
      setSelectedSchoolId(currentUser.school_id);
    }
  }, [currentUser?.school_id]);

  const loadSchoolDetails = useCallback(async (schoolId) => {
    if (!schoolId) return;
    const [membersRes, invitesRes] = await Promise.all([
      fetch(`/api/schools/${encodeURIComponent(schoolId)}/members`, { headers: authHeaders() }),
      fetch(`/api/schools/${encodeURIComponent(schoolId)}/invites`, { headers: authHeaders() })
    ]);
    const membersData = await membersRes.json();
    const invitesData = await invitesRes.json();
    if (!membersRes.ok) throw new Error(membersData.message || 'Failed to load members');
    if (!invitesRes.ok) throw new Error(invitesData.message || 'Failed to load invites');
    setMembers(membersData.members || []);
    setInvites(invitesData.invites || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError('');
        await loadSchools();
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadSchools]);

  useEffect(() => {
    if (!selectedSchoolId) return;
    (async () => {
      try {
        setError('');
        await loadSchoolDetails(selectedSchoolId);
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [selectedSchoolId, loadSchoolDetails]);

  useEffect(() => {
    if (!acceptCode || !isAuthenticated) return;
    (async () => {
      try {
        const res = await fetch('/api/affiliations/accept', {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ invite_code: acceptCode })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message);
        setMessage(t('school.affiliationAccepted'));
        setSearchParams({});
        await loadSchools();
        if (data.user?.school_id) {
          setSelectedSchoolId(data.user.school_id);
        }
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [acceptCode, isAuthenticated, loadSchools, setSearchParams, t]);

  const allowedInviteRoles = useMemo(() => {
    if (isUserPresenter || currentUser?.User_Role === 'Sta') {
      return ['Stu', 'Tea', 'Sta'];
    }
    if (currentUser?.User_Role === 'Tea') return ['Stu'];
    return [];
  }, [currentUser?.User_Role, isUserPresenter]);

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    if (!selectedSchoolId) return;
    setCreatingInvite(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/schools/${encodeURIComponent(selectedSchoolId)}/invites`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ intended_role: inviteRole, label: inviteLabel.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to create invite');
      setMessage(t('school.inviteCreated'));
      setInviteLabel('');
      await loadSchoolDetails(selectedSchoolId);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleRevoke = async (inviteId) => {
    if (!selectedSchoolId) return;
    try {
      const res = await fetch(
        `/api/schools/${encodeURIComponent(selectedSchoolId)}/invites/${encodeURIComponent(inviteId)}/revoke`,
        { method: 'POST', headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      await loadSchoolDetails(selectedSchoolId);
    } catch (err) {
      setError(err.message);
    }
  };

  const copyLink = (url) => copyInviteLink(url, {
    onError: () => setError(t('school.copyFailed'))
  });

  if (!isUserPresenter && !isUserSchoolStaff) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <p className="text-ink-muted">{t('school.accessDenied')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-12">
        <p className="text-ink-muted">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 sm:py-12">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-ink">{t('school.dashboardTitle')}</h1>
        <p className="mt-1 text-sm text-ink-muted">{t('school.dashboardSubtitle')}</p>
      </div>

      {error ? <div className="mb-4 rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div> : null}
      {message ? <div className="mb-4 rounded-md bg-green-50 border border-green-200 text-green-700 px-4 py-3 text-sm">{message}</div> : null}

      {schools.length > 1 ? (
        <div className="mb-6 max-w-md">
          <label className="block text-sm font-medium text-ink-muted">{t('school.selectSchool')}</label>
          <ShadedSelect
            className="mt-1 block w-full"
            value={selectedSchoolId}
            onChange={setSelectedSchoolId}
            options={[
              { value: '', label: t('school.selectSchool') },
              ...schools.map((s) => ({ value: s.school_id, label: s.name }))
            ]}
          />
        </div>
      ) : null}

      {selectedSchool ? (
        <div className="space-y-8">
          <section className="bg-surface rounded-lg shadow-sm border border-edge p-6">
            <h2 className="text-lg font-semibold text-ink">{selectedSchool.name}</h2>
            <p className="text-sm text-ink-subtle mt-1">
              {t(`common.districts.${selectedSchool.district}`, { defaultValue: selectedSchool.district })}
            </p>
            {selectedSchool.email_domains?.length ? (
              <p className="mt-2 text-xs text-ink-subtle">
                {t('school.verifiedDomains')}: {selectedSchool.email_domains.join(', ')}
              </p>
            ) : null}
          </section>

          <section className="bg-surface rounded-lg shadow-sm border border-edge p-6">
            <h2 className="text-lg font-semibold text-ink mb-4">{t('school.createInvite')}</h2>
            <form onSubmit={handleCreateInvite} className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-ink-muted">{t('report.role')}</label>
                <ShadedSelect
                  className="mt-1 block w-full"
                  value={inviteRole}
                  onChange={setInviteRole}
                  options={allowedInviteRoles.map((role) => ({
                    value: role,
                    label: roleLabel(role, t)
                  }))}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-ink-muted">{t('school.inviteLabelOptional')}</label>
                <input
                  type="text"
                  value={inviteLabel}
                  onChange={(e) => setInviteLabel(e.target.value)}
                  placeholder={t('school.inviteLabelPlaceholder')}
                  className="mt-1 block w-full border border-edge-strong rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div className="sm:col-span-3">
                <button
                  type="submit"
                  disabled={creatingInvite || !allowedInviteRoles.length}
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary-hover disabled:opacity-50"
                >
                  {creatingInvite ? t('common.loading') : t('school.generateInvite')}
                </button>
              </div>
            </form>
          </section>

          <section className="bg-surface rounded-lg shadow-sm border border-edge p-6">
            <h2 className="text-lg font-semibold text-ink mb-4">{t('school.activeInvites')}</h2>
            {invites.length === 0 ? (
              <p className="text-sm text-ink-subtle">{t('school.noInvites')}</p>
            ) : (
              <div className="space-y-3">
                {invites.map((invite) => (
                  <div key={invite.invite_id} className="border border-edge rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="font-mono font-semibold text-[#29496a]">{invite.code}</p>
                      <p className="text-sm text-ink-muted mt-1">
                        {roleLabel(invite.intended_role, t)}
                        {invite.label ? ` · ${invite.label}` : ''}
                      </p>
                      <p className="text-xs text-ink-subtle mt-1 break-all">{invite.join_url}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => copyLink(invite.join_url)}
                        className="px-3 py-1.5 text-sm border border-edge-strong rounded-md hover:bg-surface-muted"
                      >
                        {t('school.copy')}
                      </button>
                      {invite.active ? (
                        <button
                          type="button"
                          onClick={() => handleRevoke(invite.invite_id)}
                          className="px-3 py-1.5 text-sm border border-red-200 text-red-700 rounded-md hover:bg-red-50"
                        >
                          {t('school.revoke')}
                        </button>
                      ) : (
                        <span className="px-3 py-1.5 text-sm text-ink-subtle">{t('school.inactive')}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="bg-surface rounded-lg shadow-sm border border-edge p-6">
            <h2 className="text-lg font-semibold text-ink mb-4">{t('school.members')}</h2>
            <p className="text-sm text-ink-subtle mb-4">{t('school.membersCount', { count: members.length })}</p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-ink-subtle border-b">
                    <th className="py-2 pr-4">{t('report.name')}</th>
                    <th className="py-2 pr-4">{t('common.email')}</th>
                    <th className="py-2 pr-4">{t('report.role')}</th>
                    <th className="py-2">{t('school.affiliation')}</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <tr key={member.User_ID} className="border-b border-edge">
                      <td className="py-2 pr-4">{member.First_Name} {member.Last_Name}</td>
                      <td className="py-2 pr-4">{member.Email_Address}</td>
                      <td className="py-2 pr-4">{roleLabel(member.User_Role, t)}</td>
                      <td className="py-2">{member.affiliation_method || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      ) : (
        <p className="text-ink-muted">{t('school.noSchoolSelected')}</p>
      )}
      {linkCopiedToast}
    </div>
  );
}

export default SchoolDashboard;
